/**
 * NoteCardPopover — pulls up a note from chat as PLAIN TEXT in a lightweight
 * modal, WITHOUT opening the Drive module or its rich-text DocEditorModal
 * (a note is plain text, not a doc). Mirrors ContactCardPopover: fetch the
 * object by id, render a focused modal overlay. Read-only peek; "Open in Notes"
 * jumps to the standalone Notes module for editing.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { http } from '@/lib/http';
import { useSession } from '@/features/auth/hooks/useSession';
import { ModalPortal } from '@/common/components/ModalPortal';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { useModalOverlayClick } from '@/common/hooks/useModalOverlayClick';
import { formatRelativeTime } from '@/lib/timeUtils';
import './NoteCardPopover.css';

interface Props {
  noteId: string;
  onClose: () => void;
  /** Open the note in the standalone Notes module (for full editing). */
  onOpenInModule: (noteId: string) => void;
}

interface NoteView {
  title: string;
  body: string;
  updatedAt?: string;
}

export function NoteCardPopover({ noteId, onClose, onOpenInModule }: Props) {
  const { token } = useSession();
  const [note, setNote] = useState<NoteView | null>(null);
  const [error, setError] = useState(false);

  useEscapeKey(onClose, true);

  // Ignore the overlay click that is really the second half of the double-click
  // that opened this popover. A double-click on a note card fires click 1 (opens
  // the peek) then click 2, which lands on the freshly-rendered full-screen
  // overlay and would otherwise close it instantly. Double-clicks should just
  // open, matching single-click. The close button and Escape still close
  // immediately — only the overlay click-to-close is guarded.
  const openedAtRef = useRef(Date.now());
  const guardedOverlayClose = useCallback(() => {
    if (Date.now() - openedAtRef.current < 400) return;
    onClose();
  }, [onClose]);
  const [contentRef, handleOverlayClick, handleOverlayMouseDown] = useModalOverlayClick<HTMLDivElement>(guardedOverlayClose);

  // Stable close ref so the fetch effect doesn't re-run on parent re-render.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await http.get<any>(`/notes/${noteId}`, { authToken: token });
        if (cancelled) return;
        const raw = res.data ?? {};
        // The API flattens attributes onto the top level.
        const body = String(raw.body ?? raw.attributes?.body ?? '');
        // Derive a heading from the stored title, else the first non-empty line.
        const firstLine = body.split('\n').map((l: string) => l.trim()).find((l: string) => l.length) ?? '';
        const title = String(raw.title ?? '').trim() || firstLine.slice(0, 80) || 'Untitled note';
        setNote({ title, body, updatedAt: raw.updatedAt ?? raw.updated_at });
      } catch (err) {
        if (cancelled) return;
        console.warn('[NoteCardPopover] Failed to fetch note:', err);
        setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [noteId, token]);

  return (
    <ModalPortal>
      <div
        className="note-peek-overlay"
        onClick={handleOverlayClick}
        onMouseDown={handleOverlayMouseDown}
        role="presentation"
      >
        <div className="note-peek" ref={contentRef} role="dialog" aria-modal="true" aria-label="Note">
          <div className="note-peek-bar">
            <span className="note-peek-meta">
              {note?.updatedAt ? `${formatRelativeTime(note.updatedAt)} · ` : ''}plain text
            </span>
            <button type="button" className="note-peek-close" onClick={onClose} aria-label="Close" title="Close">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="note-peek-scroll xv-scroll">
            {error ? (
              <div className="note-peek-empty">Couldn’t load this note.</div>
            ) : note === null ? (
              <div className="note-peek-empty">Loading…</div>
            ) : (
              <>
                <div className="note-peek-title">{note.title}</div>
                {note.body.trim() ? (
                  <div className="note-peek-body">{note.body}</div>
                ) : (
                  <div className="note-peek-empty">This note is empty.</div>
                )}
              </>
            )}
          </div>
          <div className="note-peek-foot">
            <button type="button" className="note-peek-open" onClick={() => onOpenInModule(noteId)}>
              Open in Notes
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
